import { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import AppBar from "../../../components/atoms/appbar";
import Button from "../../../components/atoms/button";
import SelectBox from "../../../components/atoms/select";
import Input from "../../../components/atoms/input";
import { useSettingTournamentApplyDetail } from "../../../@apis/hooks/useSetting";
import { useBankCodes } from "../../../@apis/hooks/usePay";
import { putTournamentPayCancel } from "../../../@apis/v1/payment.service";
import type { OnErrorResponse } from "../../../@apis/dto/response/common.response";
import { useModalStore } from "../../../lib/store/useModalStore";
import { cn } from "../../../lib/utils/cn";

const CANCEL_REASONS = [
  "개인 사정",
  "일정 변경",
  "참가 불가",
  "다른 대회 참가",
  "교통/거리 문제",
  "기타(직접 입력)",
] as const;
const ETC_REASON = "기타(직접 입력)";

// 환불 계좌 입력 제한
const HOLDER_MAX = 10;
const ACCOUNT_NO_MAX = 16;

// 라벨(회색 w-80) + 값(검정) 한 줄
function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-[8px] items-start">
      <p className="min-w-[80px] shrink-0 text-[#8e8e93]">{label}</p>
      <p className="text-black break-keep whitespace-pre-line [overflow-wrap:anywhere]">
        {value}
      </p>
    </div>
  );
}

export default function CompetitionApplyCancel() {
  const navigate = useNavigate();
  const { id, applyId } = useParams();
  const { openModal } = useModalStore();
  const queryClient = useQueryClient();

  const { data: tournamentInfo } = useQuery({
    ...useSettingTournamentApplyDetail(id!, applyId!),
    enabled: !!id && !!applyId,
  });
  const tournament = tournamentInfo?.data?.tournamentInfo;
  const paymentInfo = tournamentInfo?.data?.paymentInfo;
  const isVbank = paymentInfo?.payMethod === "VBANK";

  const { data: bankCodesRes } = useQuery({
    ...useBankCodes(),
    enabled: isVbank,
  });
  const bankList = bankCodesRes?.data ?? [];

  const totalAmount = paymentInfo?.amt ?? 0;
  // TODO: 위약금/환불예정금액은 서버 환불 정보 기준으로 표기 필요(현재 결제금액 기준 임시)
  const penalty = 0;
  const refundAmount = totalAmount - penalty;

  const [reason, setReason] = useState("");
  const [etcText, setEtcText] = useState("");
  const [bank, setBank] = useState(""); // 선택된 은행 코드 (직접 선택 시에만 설정)
  const [bankQuery, setBankQuery] = useState(""); // 은행명 검색 입력값
  const [bankOpen, setBankOpen] = useState(false); // 은행 목록 노출 여부
  const [accountNumber, setAccountNumber] = useState("");
  const [holder, setHolder] = useState("");

  // 입력 키워드가 포함된 은행만 필터링 (검색어 없으면 전체)
  const filteredBanks = bankQuery.trim()
    ? bankList.filter((b) => b.name.includes(bankQuery.trim()))
    : bankList;

  // 미입력 안내 시 스크롤 이동 대상
  const bankRef = useRef<HTMLDivElement>(null);
  const accountRef = useRef<HTMLDivElement>(null);
  const holderRef = useRef<HTMLDivElement>(null);
  const reasonRef = useRef<HTMLDivElement>(null);

  const isEtc = reason === ETC_REASON;

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      putTournamentPayCancel(applyId!, {
        cancelReason: isEtc ? etcText.trim() : reason,
        // 가상계좌 환불 시 환불 계좌 정보 함께 전달
        ...(isVbank
          ? {
              refundBankCode: bank,
              refundAccountNo: accountNumber.trim(),
              refundAccountHolder: holder.trim(),
            }
          : {}),
      }),
    onSuccess: () => {
      toast("접수가 취소되었습니다.");
      // 접수 상세가 곧 취소/환불 상세이므로, 최신 상태로 다시 조회한 뒤 되돌아간다.
      queryClient.invalidateQueries({
        queryKey: ["setting-tournament-apply-detail", id, applyId],
      });
      navigate(-1);
    },
    onError: (err: OnErrorResponse) => {
      openModal("ALERT_DIALOG", {
        title: "접수 취소 실패",
        content:
          err?.response?.data?.error?.message ?? "잠시 후 다시 시도해 주세요.",
      });
    },
  });

  // 미입력 항목 안내 후 해당 영역으로 스크롤 이동
  const alertAndScroll = (
    message: string,
    ref: React.RefObject<HTMLDivElement | null>,
  ) => {
    openModal("ALERT_DIALOG", {
      title: message,
      onConfirm: () =>
        ref.current?.scrollIntoView({ behavior: "smooth", block: "center" }),
    });
  };

  // 상단부터 순서대로 필수 항목 검증 → 모두 입력 시 취소 확인
  const handleSubmit = () => {
    if (isPending) return;

    if (isVbank) {
      if (!bank) return alertAndScroll("환불 은행을 선택해 주세요.", bankRef);
      if (!accountNumber.trim())
        return alertAndScroll("환불 계좌번호를 입력해 주세요.", accountRef);
      if (!holder.trim())
        return alertAndScroll("예금주명을 입력해 주세요.", holderRef);
    }
    if (!reason) return alertAndScroll("취소 사유를 선택해 주세요.", reasonRef);
    if (isEtc && !etcText.trim())
      return alertAndScroll("취소 사유를 입력해 주세요.", reasonRef);

    openModal("CONFIRM_DIALOG", {
      title: "대회 접수를 취소할까요?",
      content:
        "접수 취소 후에는 되돌릴 수 없습니다.\n환불 수단에 따른 환불 절차가 진행됩니다.",
      rightTitle: "확인",
      onClick: () => mutate(),
    });
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#f2f2f7]">
      <AppBar backButton title="접수 취소" />

      <div className="flex-1 flex flex-col gap-[12px] px-[12px] py-[20px] pb-20">
        {/* 환불 정보 */}
        <div className="bg-white border border-[#e5e5ea] rounded-[10px] px-[16px] py-[20px] flex flex-col gap-[20px]">
          <p className="text-base font-semibold text-black">환불 정보</p>

          <div className="flex flex-col gap-[8px] text-sm font-medium">
            <Row label="환불대상" value={tournament?.name || "-"} />
            <Row
              label="환불수단"
              value={
                paymentInfo?.payMethodLabel ??
                (isVbank ? "가상계좌" : "신용카드")
              }
            />
            <Row
              label="총 결제금액"
              value={`${totalAmount.toLocaleString()}원`}
            />
            <Row label="위약금" value={`${penalty.toLocaleString()}원`} />
          </div>

          <div className="bg-[#f2f2f7] rounded-[10px] p-[8px] flex items-center justify-between">
            <p className="text-base font-semibold text-black">환불 예정 금액</p>
            <p className="text-xl font-medium text-[#ff3b30]">
              {refundAmount.toLocaleString()}원
            </p>
          </div>

          <div className="bg-[#fff7e5] rounded-[10px] px-[12px] py-[8px]">
            <p className="text-xs font-medium text-[#a2845e] break-keep">
              {isVbank
                ? "입력된 환불 계좌로 환불이 진행됩니다. 환불 완료까지 영업일 기준 3~5일 정도 소요될 수 있습니다."
                : "카드사 정책에 따라 환불 완료까지 영업일 기준 3~5일 정도 소요될 수 있습니다."}
            </p>
          </div>
        </div>

        {/* 환불 계좌 정보 (가상계좌) */}
        {isVbank && (
          <div className="bg-white border border-[#e5e5ea] rounded-[10px] px-[16px] py-[20px] flex flex-col gap-[12px]">
            <p className="text-base font-semibold text-black">환불 계좌 정보</p>

            <div ref={bankRef} className="relative flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">
                은행명
              </label>
              <input
                type="text"
                value={bankQuery}
                placeholder="은행명 검색"
                onChange={(e) => {
                  setBankQuery(e.target.value);
                  // 텍스트만 입력한 상태는 미선택으로 처리 (직접 선택해야 저장 가능)
                  setBank("");
                  setBankOpen(true);
                }}
                onFocus={() => setBankOpen(true)}
                onBlur={() => setTimeout(() => setBankOpen(false), 150)}
                className={`w-full h-[50px] rounded-[10px] border border-[#e6e6e6] p-5 font-[500] text-sm outline-none focus:ring-1 focus:ring-black focus:border-black ${
                  bank ? "text-black" : "text-[#ccc]"
                }`}
              />
              {bankOpen && (
                <ul className="absolute top-full left-0 right-0 z-20 mt-[4px] max-h-[220px] overflow-y-auto rounded-[10px] border border-[#e6e6e6] bg-white shadow-md">
                  {filteredBanks.length > 0 ? (
                    filteredBanks.map((b) => (
                      <li key={b.code}>
                        <button
                          type="button"
                          // blur보다 먼저 선택되도록 mousedown 기본동작 방지
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setBank(b.code);
                            setBankQuery(b.name);
                            setBankOpen(false);
                          }}
                          className={`w-full text-left px-[16px] py-[12px] text-sm hover:bg-[#f2f2f7] ${
                            bank === b.code
                              ? "text-[#007aff] font-medium"
                              : "text-black"
                          }`}
                        >
                          {b.name}
                        </button>
                      </li>
                    ))
                  ) : (
                    <li className="px-[16px] py-[12px] text-sm text-[#8e8e93]">
                      검색 결과가 없습니다.
                    </li>
                  )}
                </ul>
              )}
            </div>

            <div ref={accountRef}>
              <Input
                label="계좌번호"
                placeholder="숫자만 입력"
                inputMode="numeric"
                maxLength={ACCOUNT_NO_MAX}
                value={accountNumber}
                onChange={(e) =>
                  setAccountNumber(
                    e.target.value
                      .replace(/[^0-9]/g, "")
                      .slice(0, ACCOUNT_NO_MAX),
                  )
                }
              />
            </div>
            <div ref={holderRef}>
              <Input
                label="예금주명"
                placeholder="예금주명 입력"
                maxLength={HOLDER_MAX}
                value={holder}
                onChange={(e) => setHolder(e.target.value.slice(0, HOLDER_MAX))}
              />
            </div>
          </div>
        )}

        {/* 취소 사유 */}
        <div
          ref={reasonRef}
          className="bg-white border border-[#e5e5ea] rounded-[10px] px-[16px] py-[20px] flex flex-col gap-[10px]"
        >
          <div className="flex flex-col gap-[4px]">
            <label className="text-sm font-medium text-gray-700">
              취소 사유
            </label>
            <SelectBox
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                // 사유 변경 시 기타 입력값 초기화
                setEtcText("");
              }}
              options={[
                { label: "취소 사유 선택", value: "" },
                ...CANCEL_REASONS.map((r) => ({ label: r, value: r })),
              ]}
              className={cn(
                "text-sm h-auto py-[12px] px-[16px]",
                reason ? "text-black" : "text-[#ccc]",
              )}
            />
          </div>

          {isEtc && (
            <div className="flex flex-col gap-[4px] items-end">
              <p className="text-xs font-medium text-[#8e8e93]">
                <span className="text-black">{etcText.length}</span>/100
              </p>
              <textarea
                value={etcText}
                onChange={(e) => setEtcText(e.target.value.slice(0, 100))}
                maxLength={100}
                placeholder="취소 사유를 입력해 주세요."
                className="w-full h-[80px] p-[12px] rounded-[10px] border border-[#e6e6e6] text-sm resize-none focus:outline-none focus:border-[#007aff]"
              />
            </div>
          )}
        </div>
      </div>

      {/* 하단 고정 액션 */}
      <div className="bg-white p-[12px] fixed max-w-[440px] w-full bottom-0 z-50 flex gap-[8px]">
        <Button
          type="button"
          className="flex-1 font-normal text-base"
          variant="white"
          label="이전"
          onClick={() => navigate(-1)}
        />
        <Button
          type="button"
          className="flex-1 font-normal text-base whitespace-pre-line break-keep"
          variant={isPending ? "disabled" : "red"}
          label="접수 취소하기"
          disabled={isPending}
          onClick={handleSubmit}
        />
      </div>
    </div>
  );
}
